import { BLACK, type Color } from '@baduk/engine';
import type { GameSetup } from './components/GamePanel.js';

/**
 * Who moves next, and after how long.
 *
 * This is pulled out of the component because it is the one piece of the move
 * loop with real logic in it: playing, watching, paused, stepping and thinking
 * all interact, and getting it wrong means either a frozen board or a runaway
 * loop. As a plain function it can be tested without a browser.
 */

export interface TurnState {
  readonly setup: GameSetup;
  /** Whose turn it is on the board. */
  readonly toPlay: Color;
  readonly isOver: boolean;
  /** A request is already in flight. */
  readonly thinking: boolean;
  /** Only meaningful while watching. */
  readonly paused: boolean;
  /** Moves the watcher has asked for while paused. */
  readonly steps: number;
}

export interface BotTurn {
  readonly botId: string;
  /** Wait before asking, so a game of instant bots stays followable. */
  readonly delayMs: number;
}

/** The bot that owns a colour, or null when a human does. */
export function botIdFor(setup: GameSetup, color: Color): string | null {
  if (setup.mode === 'watch') return color === BLACK ? setup.botId : setup.whiteBotId;
  const botIsBlack = !setup.humanIsBlack;
  return (color === BLACK) === botIsBlack ? setup.botId : null;
}

/** The move to ask for now, or null if nothing should happen. */
export function nextBotTurn(state: TurnState): BotTurn | null {
  const { setup, toPlay, isOver, thinking, paused, steps } = state;
  if (isOver || thinking) return null;

  const botId = botIdFor(setup, toPlay);
  if (botId === null) return null;

  // Playing, a bot always answers: pausing your opponent is not a feature. The
  // pause and step controls exist only for watching, where the loop is the show.
  if (setup.mode === 'play') return { botId, delayMs: 0 };

  if (paused && steps === 0) return null;
  // A requested single move happens straight away — the viewer already waited.
  return { botId, delayMs: steps > 0 ? 0 : setup.moveDelayMs };
}
